/**
 * Pattern Admin Dashboard
 * =======================
 * 
 * Admin interface for managing classification patterns.
 * Features:
 * - List all patterns with search/filter
 * - Create/Edit/Delete patterns
 * - Pattern testing tool
 * - AI suggestion tool
 * - Pattern candidate review
 * - Import/Export patterns
 */

import React, { useState, useEffect, useMemo } from 'react';
import { 
  Search, Plus, Edit2, Trash2, Check, X, Download, Upload, 
  Lightbulb, TestTube, Brain, TrendingUp, ChevronDown, ChevronRight,
  AlertTriangle, Filter, RefreshCw, Sparkles, Target, Award
} from 'lucide-react';
import api from '../../utils/api';

// ============================================================================
// TYPES
// ============================================================================

interface Pattern {
  _id: string;
  pattern_id: string;
  event_id: string;
  event_type: string;
  match_keywords: string[];
  match_mcc: string[];
  amount_range: [number, number] | null;
  generic_category: string;
  correct_category: string;
  correct_subcategory: string;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  rationale: string;
  active: boolean;
  accept_count: number;
  reject_count: number;
  total_decisions: number;
  accept_rate: number | null;
  created_at: string;
  updated_at: string;
}

interface PatternCandidate {
  candidate_id: string;
  event_id: string;
  suggested_category: string;
  suggested_subcategory: string;
  suggested_keywords: string[];
  occurrence_count: number;
  source_reports: string[];
  sample_vendors: string[];
  status: string;
  created_at: string;
}

interface PatternTestResult {
  pattern_id: string;
  match_score: number;
  matched_keywords: string[];
  suggested_category: string;
  suggested_subcategory: string;
  confidence: string;
  rationale: string;
}

// ============================================================================
// PATTERN FORM MODAL
// ============================================================================

interface PatternFormProps {
  pattern?: Pattern | null;
  onSave: (data: any) => Promise<void>;
  onClose: () => void;
}

function PatternFormModal({ pattern, onSave, onClose }: PatternFormProps) {
  const [formData, setFormData] = useState({
    pattern_id: pattern?.pattern_id || '',
    event_id: pattern?.event_id || 'smitty_supply_2025',
    event_type: pattern?.event_type || 'business_interruption',
    match_keywords: pattern?.match_keywords?.join(', ') || '',
    correct_category: pattern?.correct_category || '',
    correct_subcategory: pattern?.correct_subcategory || '',
    confidence: pattern?.confidence || 'MEDIUM',
    rationale: pattern?.rationale || '',
    active: pattern?.active ?? true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const data = {
        ...formData,
        match_keywords: formData.match_keywords.split(',').map(k => k.trim()).filter(k => k)
      };
      await onSave(data);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save pattern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <div className="sticky top-0 border-b px-6 py-4 flex items-center justify-between" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
          <h2 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
            {pattern ? 'Edit Pattern' : 'Create Pattern'}
          </h2>
          <button onClick={onClose} style={{ color: 'var(--color-text-dim)' }}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="border px-4 py-3 rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}>
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Pattern ID *
              </label>
              <input
                type="text"
                value={formData.pattern_id}
                onChange={e => setFormData({...formData, pattern_id: e.target.value})}
                disabled={!!pattern}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color:var(--color-brand)] disabled:bg-[color:var(--color-bg-elevated)]"
                placeholder="e.g., SM-026"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Event ID *
              </label>
              <input
                type="text"
                value={formData.event_id}
                onChange={e => setFormData({...formData, event_id: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color:var(--color-brand)]"
                placeholder="e.g., smitty_supply_2025"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
              Match Keywords * (comma-separated)
            </label>
            <textarea
              value={formData.match_keywords}
              onChange={e => setFormData({...formData, match_keywords: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color:var(--color-brand)]"
              rows={2}
              placeholder="keyword1, keyword2, keyword3"
              required
            />
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
              Enter keywords that should trigger this pattern match
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Correct Category *
              </label>
              <input
                type="text"
                value={formData.correct_category}
                onChange={e => setFormData({...formData, correct_category: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color:var(--color-brand)]"
                placeholder="e.g., Environmental Remediation"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Correct Subcategory *
              </label>
              <input
                type="text"
                value={formData.correct_subcategory}
                onChange={e => setFormData({...formData, correct_subcategory: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color:var(--color-brand)]"
                placeholder="e.g., Soil Remediation"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Confidence Level
              </label>
              <select
                value={formData.confidence}
                onChange={e => setFormData({...formData, confidence: e.target.value as 'HIGH' | 'MEDIUM' | 'LOW'})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color:var(--color-brand)]"
              >
                <option value="HIGH">HIGH - Very reliable</option>
                <option value="MEDIUM">MEDIUM - Generally reliable</option>
                <option value="LOW">LOW - Needs review</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={e => setFormData({...formData, active: e.target.checked})}
                  className="w-5 h-5 rounded border-[color:var(--color-border)]"
                />
                <span className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Active</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
              Rationale * (Court-defensible explanation)
            </label>
            <textarea
              value={formData.rationale}
              onChange={e => setFormData({...formData, rationale: e.target.value})}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color:var(--color-brand)]"
              rows={3}
              placeholder="Explain why this classification is appropriate..."
              required
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 rounded-lg disabled:opacity-50 flex items-center gap-2"
              style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            >
              {saving ? (
                <>
                  <RefreshCw size={16} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check size={16} />
                  Save Pattern
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// PATTERN TESTER
// ============================================================================

interface PatternTesterProps {
  onClose: () => void;
}

function PatternTester({ onClose }: PatternTesterProps) {
  const [vendor, setVendor] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [eventId, setEventId] = useState('smitty_supply_2025');
  const [results, setResults] = useState<PatternTestResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState<any>(null);
  const [loadingAi, setLoadingAi] = useState(false);

  const handleTest = async () => {
    setLoading(true);
    setResults([]);
    setAiSuggestion(null);
    
    try {
      const response = await api.post('/api/v1/pattern-admin/test', {
        vendor,
        description,
        amount: amount ? parseFloat(amount) : null,
        event_id: eventId
      });
      setResults(response.matches || []);
    } catch (err) {
      console.error('Pattern test failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAiSuggest = async () => {
    setLoadingAi(true);
    
    try {
      const response = await api.post('/api/v1/pattern-admin/ai-suggest', {
        vendor,
        description,
        amount: parseFloat(amount) || 0,
        current_category: null,
        event_context: 'business_interruption'
      });
      setAiSuggestion(response);
    } catch (err) {
      console.error('AI suggestion failed:', err);
    } finally {
      setLoadingAi(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <div className="sticky top-0 px-6 py-4 flex items-center justify-between rounded-t-xl" style={{ backgroundColor: 'var(--color-brand)' }}>
          <div className="flex items-center gap-3">
            <TestTube style={{ color: 'var(--color-on-brand)' }} size={24} />
            <h2 className="text-xl font-bold" style={{ color: 'var(--color-on-brand)' }}>Pattern Testing Tool</h2>
          </div>
          <button onClick={onClose} style={{ color: 'var(--color-on-brand)' }}>
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          {/* Input Form */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Vendor Name
              </label>
              <input
                type="text"
                value={vendor}
                onChange={e => setVendor(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color:var(--color-brand)]"
                placeholder="e.g., EnviroClean Services"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Amount
              </label>
              <input
                type="number"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color:var(--color-brand)]"
                placeholder="e.g., 15000"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Description
              </label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color:var(--color-brand)]"
                rows={2}
                placeholder="e.g., Hazmat cleanup and soil remediation after explosion"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                Event ID
              </label>
              <input
                type="text"
                value={eventId}
                onChange={e => setEventId(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color:var(--color-brand)]"
                placeholder="smitty_supply_2025"
              />
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={handleTest}
                disabled={!vendor || loading}
                className="flex-1 px-4 py-2 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              >
                {loading ? <RefreshCw size={16} className="animate-spin" /> : <Target size={16} />}
                Test Patterns
              </button>
              <button
                onClick={handleAiSuggest}
                disabled={!vendor || loadingAi}
                className="flex-1 px-4 py-2 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              >
                {loadingAi ? <RefreshCw size={16} className="animate-spin" /> : <Brain size={16} />}
                AI Suggest
              </button>
            </div>
          </div>

          {/* Results */}
          <div className="grid grid-cols-2 gap-6">
            {/* Pattern Matches */}
            <div>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                <Target size={18} style={{ color: 'var(--color-needs-human-text)' }} />
                Pattern Matches ({results.length})
              </h3>

              {results.length === 0 && !loading && (
                <div className="text-center py-8 rounded-lg" style={{ color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-base)' }}>
                  <Lightbulb size={32} className="mx-auto mb-2" style={{ color: 'var(--color-text-dim)' }} />
                  <p>No matches found</p>
                  <p className="text-sm">Try different keywords or use AI suggestion</p>
                </div>
              )}
              
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {results.map((result, idx) => (
                  <div
                    key={result.pattern_id}
                    className="p-4 rounded-lg border"
                    style={
                      idx === 0
                        ? { borderColor: 'var(--color-verified)', backgroundColor: 'transparent' }
                        : { borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface)' }
                    }
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{result.pattern_id}</span>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 rounded text-xs font-medium" style={
                          result.confidence === 'HIGH' ? { backgroundColor: 'transparent', color: 'var(--color-verified)' } :
                          result.confidence === 'MEDIUM' ? { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-brand)' } :
                          { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-brand)' }
                        }>
                          {result.confidence}
                        </span>
                        <span className="text-sm font-medium" style={{ color: 'var(--color-needs-human-text)' }}>
                          {(result.match_score * 100).toFixed(0)}%
                        </span>
                      </div>
                    </div>
                    <div className="text-sm mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                      <span className="font-medium">{result.suggested_category}</span>
                      {' > '}
                      <span>{result.suggested_subcategory}</span>
                    </div>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {result.matched_keywords.map((kw, i) => (
                        <span key={i} className="px-1.5 py-0.5 rounded text-xs" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                          {kw}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs italic" style={{ color: 'var(--color-text-muted)' }}>{result.rationale}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* AI Suggestion */}
            <div>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                <Brain size={18} style={{ color: 'var(--color-needs-human-text)' }} />
                AI Suggestion
              </h3>

              {!aiSuggestion && !loadingAi && (
                <div className="text-center py-8 rounded-lg" style={{ color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-base)' }}>
                  <Sparkles size={32} className="mx-auto mb-2" style={{ color: 'var(--color-text-dim)' }} />
                  <p>No AI suggestion yet</p>
                  <p className="text-sm">Click "AI Suggest" to get Claude's recommendation</p>
                </div>
              )}

              {loadingAi && (
                <div className="text-center py-8 rounded-lg" style={{ color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-base)' }}>
                  <RefreshCw size={32} className="mx-auto mb-2 animate-spin" style={{ color: 'var(--color-needs-human-text)' }} />
                  <p>Analyzing with Claude...</p>
                </div>
              )}

              {aiSuggestion && (
                <div className="space-y-4">
                  <div className="p-4 rounded-lg border" style={{ borderColor: 'var(--color-needs-human-tint-bd)', backgroundColor: 'var(--color-needs-human-tint)' }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Primary Suggestion</span>
                      <span className="text-sm font-medium" style={{ color: 'var(--color-needs-human-text)' }}>
                        {(aiSuggestion.confidence * 100).toFixed(0)}% confidence
                      </span>
                    </div>
                    <div className="text-sm mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                      <span className="font-medium">{aiSuggestion.suggested_category}</span>
                      {' > '}
                      <span>{aiSuggestion.suggested_subcategory}</span>
                    </div>
                    <p className="text-sm bg-white/50 p-2 rounded" style={{ color: 'var(--color-text-secondary)' }}>
                      {aiSuggestion.rationale}
                    </p>
                  </div>

                  {aiSuggestion.alternative_categories?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Alternatives:</h4>
                      <div className="space-y-2">
                        {aiSuggestion.alternative_categories.map((alt: any, idx: number) => (
                          <div key={idx} className="p-3 rounded-lg text-sm" style={{ backgroundColor: 'var(--color-bg-base)' }}>
                            <div className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                              {alt.category} {'>'} {alt.subcategory}
                            </div>
                            <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>{alt.reason}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN ADMIN DASHBOARD
// ============================================================================

export default function PatternAdminDashboard() {
  const [patterns, setPatterns] = useState<Pattern[]>([]);
  const [candidates, setCandidates] = useState<PatternCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // UI State
  const [searchQuery, setSearchQuery] = useState('');
  const [filterEvent, setFilterEvent] = useState('');
  const [filterConfidence, setFilterConfidence] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [activeTab, setActiveTab] = useState<'patterns' | 'candidates' | 'stats'>('patterns');
  
  // Modals
  const [showPatternForm, setShowPatternForm] = useState(false);
  const [editingPattern, setEditingPattern] = useState<Pattern | null>(null);
  const [showTester, setShowTester] = useState(false);
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set(['smitty_supply_2025']));

  useEffect(() => {
    fetchData();
  }, [showInactive]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [patternsRes, candidatesRes] = await Promise.all([
        api.get(`/api/v1/pattern-admin/patterns?include_inactive=${showInactive}`),
        api.get('/api/v1/pattern-admin/candidates')
      ]);
      setPatterns(patternsRes.patterns || []);
      setCandidates(candidatesRes.candidates || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Group patterns by event
  const patternsByEvent = useMemo(() => {
    const groups: Record<string, Pattern[]> = {};
    patterns.forEach(p => {
      const event = p.event_id || 'unknown';
      if (!groups[event]) groups[event] = [];
      groups[event].push(p);
    });
    return groups;
  }, [patterns]);

  // Filter patterns
  const filteredPatterns = useMemo(() => {
    return patterns.filter(p => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matches = 
          p.pattern_id.toLowerCase().includes(query) ||
          p.correct_category.toLowerCase().includes(query) ||
          p.correct_subcategory.toLowerCase().includes(query) ||
          p.match_keywords.some(k => k.toLowerCase().includes(query));
        if (!matches) return false;
      }
      if (filterEvent && p.event_id !== filterEvent) return false;
      if (filterConfidence && p.confidence !== filterConfidence) return false;
      return true;
    });
  }, [patterns, searchQuery, filterEvent, filterConfidence]);

  // Handlers
  const handleSavePattern = async (data: any) => {
    if (editingPattern) {
      await api.put(`/api/v1/pattern-admin/patterns/${editingPattern.pattern_id}`, data);
    } else {
      await api.post('/api/v1/pattern-admin/patterns', data);
    }
    fetchData();
  };

  const handleDeletePattern = async (patternId: string) => {
    if (!confirm(`Are you sure you want to deactivate pattern ${patternId}?`)) return;
    await api.delete(`/api/v1/pattern-admin/patterns/${patternId}`);
    fetchData();
  };

  const handlePromoteCandidate = async (candidateId: string) => {
    const patternId = prompt('Enter Pattern ID for the new pattern (e.g., DISC-001):');
    if (!patternId) return;
    
    await api.post(`/api/v1/pattern-admin/candidates/${candidateId}/promote?pattern_id=${patternId}`);
    fetchData();
  };

  const handleRejectCandidate = async (candidateId: string) => {
    const reason = prompt('Reason for rejection (optional):');
    await api.post(`/api/v1/pattern-admin/candidates/${candidateId}/reject?reason=${encodeURIComponent(reason || '')}`);
    fetchData();
  };

  const toggleEventExpanded = (eventId: string) => {
    const newExpanded = new Set(expandedEvents);
    if (newExpanded.has(eventId)) {
      newExpanded.delete(eventId);
    } else {
      newExpanded.add(eventId);
    }
    setExpandedEvents(newExpanded);
  };

  // Stats
  const stats = useMemo(() => {
    const total = patterns.length;
    const active = patterns.filter(p => p.active).length;
    const highConf = patterns.filter(p => p.confidence === 'HIGH' && p.active).length;
    const withDecisions = patterns.filter(p => p.total_decisions > 0);
    const avgAcceptRate = withDecisions.length > 0 
      ? withDecisions.reduce((sum, p) => sum + (p.accept_rate || 0), 0) / withDecisions.length 
      : 0;
    
    return { total, active, highConf, avgAcceptRate, pendingCandidates: candidates.length };
  }, [patterns, candidates]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw size={32} className="animate-spin" style={{ color: 'var(--color-needs-human-text)' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: 'var(--color-bg-base)' }}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3" style={{ color: 'var(--color-text-primary)' }}>
              <Lightbulb style={{ color: 'var(--color-brand)' }} size={28} />
              Pattern Library Admin
            </h1>
            <p className="mt-1" style={{ color: 'var(--color-text-secondary)' }}>Manage classification patterns and review discoveries</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowTester(true)}
              className="px-4 py-2 rounded-lg flex items-center gap-2"
              style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              data-testid="open-pattern-tester"
            >
              <TestTube size={18} />
              Test Patterns
            </button>
            <button
              onClick={() => { setEditingPattern(null); setShowPatternForm(true); }}
              className="px-4 py-2 rounded-lg flex items-center gap-2"
              style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              data-testid="create-pattern-btn"
            >
              <Plus size={18} />
              Add Pattern
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-5 gap-4 mb-6">
          <div className="rounded-lg shadow p-4" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
            <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Total Patterns</div>
            <div className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{stats.total}</div>
          </div>
          <div className="rounded-lg shadow p-4" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
            <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Active</div>
            <div className="text-2xl font-bold" style={{ color: 'var(--color-verified)' }}>{stats.active}</div>
          </div>
          <div className="rounded-lg shadow p-4" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
            <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>High Confidence</div>
            <div className="text-2xl font-bold" style={{ color: 'var(--color-needs-human-text)' }}>{stats.highConf}</div>
          </div>
          <div className="rounded-lg shadow p-4" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
            <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Avg Accept Rate</div>
            <div className="text-2xl font-bold" style={{ color: 'var(--color-needs-human-text)' }}>
              {stats.avgAcceptRate > 0 ? `${(stats.avgAcceptRate * 100).toFixed(0)}%` : 'N/A'}
            </div>
          </div>
          <div className="rounded-lg shadow p-4" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
            <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Pending Candidates</div>
            <div className="text-2xl font-bold" style={{ color: 'var(--color-brand)' }}>{stats.pendingCandidates}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <button
            onClick={() => setActiveTab('patterns')}
            className={`px-4 py-2 font-medium ${
              activeTab === 'patterns'
                ? 'border-b-2 border-[color:var(--color-brand)]'
                : ''
            }`}
            style={{ color: activeTab === 'patterns' ? 'var(--color-needs-human-text)' : 'var(--color-text-secondary)' }}
          >
            Patterns ({stats.total})
          </button>
          <button
            onClick={() => setActiveTab('candidates')}
            className={`px-4 py-2 font-medium flex items-center gap-2 ${
              activeTab === 'candidates'
                ? 'border-b-2 border-[color:var(--color-brand)]'
                : ''
            }`}
            style={{ color: activeTab === 'candidates' ? 'var(--color-needs-human-text)' : 'var(--color-text-secondary)' }}
          >
            Discovered Candidates
            {stats.pendingCandidates > 0 && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-brand)' }}>
                {stats.pendingCandidates}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Patterns Tab */}
      {activeTab === 'patterns' && (
        <div className="rounded-lg shadow" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
          {/* Filters */}
          <div className="p-4 border-b flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-dim)' }} size={18} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search patterns..."
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[color:var(--color-brand)]"
                />
              </div>
            </div>
            <select
              value={filterEvent}
              onChange={e => setFilterEvent(e.target.value)}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="">All Events</option>
              {Object.keys(patternsByEvent).map(event => (
                <option key={event} value={event}>{event}</option>
              ))}
            </select>
            <select
              value={filterConfidence}
              onChange={e => setFilterConfidence(e.target.value)}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="">All Confidence</option>
              <option value="HIGH">HIGH</option>
              <option value="MEDIUM">MEDIUM</option>
              <option value="LOW">LOW</option>
            </select>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={e => setShowInactive(e.target.checked)}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Show Inactive</span>
            </label>
          </div>

          {/* Pattern List by Event */}
          <div className="divide-y divide-[color:var(--color-border-faint)]">
            {Object.entries(patternsByEvent).map(([eventId, eventPatterns]) => {
              const visiblePatterns = eventPatterns.filter(p => 
                filteredPatterns.includes(p)
              );
              
              if (visiblePatterns.length === 0) return null;
              
              return (
                <div key={eventId}>
                  <button
                    onClick={() => toggleEventExpanded(eventId)}
                    className="w-full px-4 py-3 flex items-center justify-between transition-colors"
                    style={{ backgroundColor: 'var(--color-bg-base)' }}
                  >
                    <div className="flex items-center gap-3">
                      {expandedEvents.has(eventId) ? (
                        <ChevronDown size={20} style={{ color: 'var(--color-text-muted)' }} />
                      ) : (
                        <ChevronRight size={20} style={{ color: 'var(--color-text-muted)' }} />
                      )}
                      <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{eventId}</span>
                      <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>({visiblePatterns.length} patterns)</span>
                    </div>
                  </button>
                  
                  {expandedEvents.has(eventId) && (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="text-xs uppercase" style={{ backgroundColor: 'var(--color-bg-base)', color: 'var(--color-text-label)' }}>
                          <tr>
                            <th className="px-4 py-2 text-left">Pattern ID</th>
                            <th className="px-4 py-2 text-left">Keywords</th>
                            <th className="px-4 py-2 text-left">Category</th>
                            <th className="px-4 py-2 text-center">Confidence</th>
                            <th className="px-4 py-2 text-center">Decisions</th>
                            <th className="px-4 py-2 text-center">Accept Rate</th>
                            <th className="px-4 py-2 text-center">Status</th>
                            <th className="px-4 py-2 text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[color:var(--color-border-faint)]">
                          {visiblePatterns.map(pattern => (
                            <tr key={pattern.pattern_id} className={`${!pattern.active ? 'opacity-50' : ''}`}>
                              <td className="px-4 py-3 font-medium" style={{ color: 'var(--color-text-primary)' }}>
                                {pattern.pattern_id}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex flex-wrap gap-1 max-w-[200px]">
                                  {pattern.match_keywords.slice(0, 3).map((kw, i) => (
                                    <span key={i} className="px-1.5 py-0.5 rounded text-xs" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                                      {kw}
                                    </span>
                                  ))}
                                  {pattern.match_keywords.length > 3 && (
                                    <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>+{pattern.match_keywords.length - 3}</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <div className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{pattern.correct_category}</div>
                                <div style={{ color: 'var(--color-text-muted)' }}>{pattern.correct_subcategory}</div>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className="px-2 py-1 rounded text-xs font-medium" style={
                                  pattern.confidence === 'HIGH' ? { backgroundColor: 'transparent', color: 'var(--color-verified)' } :
                                  pattern.confidence === 'MEDIUM' ? { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-brand)' } :
                                  { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-brand)' }
                                }>
                                  {pattern.confidence}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                                {pattern.total_decisions || 0}
                              </td>
                              <td className="px-4 py-3 text-center">
                                {pattern.accept_rate !== null ? (
                                  <span className="text-sm font-medium" style={{
                                    color: pattern.accept_rate >= 0.8 ? 'var(--color-verified)' :
                                    pattern.accept_rate >= 0.5 ? 'var(--color-brand)' :
                                    'var(--color-needs-human-text)'
                                  }}>
                                    {(pattern.accept_rate * 100).toFixed(0)}%
                                  </span>
                                ) : (
                                  <span style={{ color: 'var(--color-text-ghost)' }}>—</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                {pattern.active ? (
                                  <span className="px-2 py-1 rounded text-xs" style={{ backgroundColor: 'transparent', color: 'var(--color-verified)' }}>Active</span>
                                ) : (
                                  <span className="px-2 py-1 rounded text-xs" style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }}>Inactive</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    onClick={() => { setEditingPattern(pattern); setShowPatternForm(true); }}
                                    className="p-1"
                                    style={{ color: 'var(--color-text-dim)' }}
                                    title="Edit"
                                  >
                                    <Edit2 size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleDeletePattern(pattern.pattern_id)}
                                    className="p-1"
                                    style={{ color: 'var(--color-text-dim)' }}
                                    title="Deactivate"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Candidates Tab */}
      {activeTab === 'candidates' && (
        <div className="rounded-lg shadow" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
          <div className="p-6">
            {candidates.length === 0 ? (
              <div className="text-center py-12" style={{ color: 'var(--color-text-muted)' }}>
                <Award size={48} className="mx-auto mb-4" style={{ color: 'var(--color-text-dim)' }} />
                <p className="text-lg font-medium">No Pattern Candidates</p>
                <p className="text-sm mt-1">
                  Candidates are discovered automatically when accountants make repeated reclassifications
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {candidates.map(candidate => (
                  <div
                    key={candidate.candidate_id}
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono text-sm px-2 py-1 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                            {candidate.candidate_id}
                          </span>
                          <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                            {candidate.occurrence_count} occurrences across {candidate.source_reports.length} reports
                          </span>
                        </div>
                        <div className="text-lg font-medium" style={{ color: 'var(--color-text-primary)' }}>
                          {candidate.suggested_category} &gt; {candidate.suggested_subcategory}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {candidate.suggested_keywords.map((kw, i) => (
                            <span key={i} className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                              {kw}
                            </span>
                          ))}
                        </div>
                        <div className="text-sm mt-2" style={{ color: 'var(--color-text-muted)' }}>
                          Sample vendors: {candidate.sample_vendors.slice(0, 3).join(', ')}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handlePromoteCandidate(candidate.candidate_id)}
                          className="px-4 py-2 rounded-lg flex items-center gap-2"
                          style={{ backgroundColor: 'var(--color-verified)', color: 'var(--color-on-brand)' }}
                        >
                          <Check size={16} />
                          Promote
                        </button>
                        <button
                          onClick={() => handleRejectCandidate(candidate.candidate_id)}
                          className="px-4 py-2 rounded-lg flex items-center gap-2"
                          style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                        >
                          <X size={16} />
                          Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      {showPatternForm && (
        <PatternFormModal
          pattern={editingPattern}
          onSave={handleSavePattern}
          onClose={() => { setShowPatternForm(false); setEditingPattern(null); }}
        />
      )}
      
      {showTester && (
        <PatternTester onClose={() => setShowTester(false)} />
      )}
    </div>
  );
}
